import { motion } from "framer-motion";
import { ArrowRight, Wallet, VideoOff, Clock3, ShieldCheck, Play } from "lucide-react";

const CHIPS = [
  { icon: Wallet, label: "Fixed monthly pricing" },
  { icon: VideoOff, label: "No shoot required — send raw files" },
  { icon: Clock3, label: "48-hour standard turnaround" },
  { icon: ShieldCheck, label: "Trakheesi & permit ready" },
];

const STATS = [
  { value: "500+", label: "Property reels delivered" },
  { value: "48h", label: "Standard turnaround" },
  { value: "100%", label: "Trakheesi compliant" },
  { value: "7 days", label: "Trial sprint delivery" },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = ({ onAudit }) => {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden stars-bg">
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#fba31b]/10 blur-[140px] pointer-events-none" />
      <div className="absolute top-20 -left-52 w-[480px] h-[480px] rounded-full bg-[#1c2e52]/60 blur-[120px] pointer-events-none" />
      <div className="orbit-ring hidden lg:block w-[700px] h-[700px] -right-64 -top-64 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 lg:pt-24 pb-16 sm:pb-20">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
          <div>
            <motion.p {...fadeUp(0)} data-testid="hero-eyebrow" className="font-code text-xs sm:text-sm uppercase tracking-[0.25em] text-[#fba31b] font-bold">
              Real Estate Creative Studio • Dubai & UAE
            </motion.p>

            <motion.h1
              {...fadeUp(0.1)}
              data-testid="hero-headline"
              className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]"
            >
              Your Real Estate <span className="text-[#fba31b]">Creative Team.</span>
              <br />
              Without the Full-Time Hiring Cost.
            </motion.h1>

            <motion.p {...fadeUp(0.2)} data-testid="hero-subcopy" className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
              We turn your raw property photos and footage into scroll-stopping reels, high-converting social creatives, listing videos and luxury brochures — delivered to your brand standards, during Dubai working hours.
            </motion.p>

            <motion.div {...fadeUp(0.3)} data-testid="hero-trust-chips" className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              {CHIPS.map((c) => (
                <div key={c.label} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#162544]/60 px-4 py-3">
                  <c.icon className="w-5 h-5 text-[#fba31b] shrink-0" />
                  <span className="text-sm text-slate-200 font-medium">{c.label}</span>
                </div>
              ))}
            </motion.div>

            <motion.div {...fadeUp(0.4)} className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-audit-btn"
                onClick={onAudit}
                className="animate-pulse-glow inline-flex items-center gap-2 bg-[#fba31b] hover:bg-[#e59312] text-[#12203c] font-bold px-7 py-4 rounded-full text-sm sm:text-base transition-colors"
              >
                Get a Free Audit + Sample Reel <ArrowRight className="w-5 h-5" />
              </button>
              <a
                data-testid="hero-explore-btn"
                href="#packages"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-[#fba31b]/60 text-white font-semibold px-7 py-4 rounded-full text-sm sm:text-base transition-colors"
              >
                Explore Packages
              </a>
            </motion.div>

            <motion.p {...fadeUp(0.5)} className="mt-5 text-xs sm:text-sm text-slate-400">
              See the quality. Experience the process. No obligation.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
            data-testid="hero-collage"
          >
            <div className="relative h-[420px] sm:h-[520px]">
              <div className="absolute left-0 top-0 w-[64%] h-full rounded-3xl overflow-hidden border border-[#fba31b]/25 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.8)]">
                <img
                  src="https://images.unsplash.com/photo-1634007626524-f47fa37810a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxkdWJhaSUyMGFyY2hpdGVjdHVyZSUyMHNreWxpbmUlMjBsdXh1cnklMjBwcm9wZXJ0eSUyMHJlYWwlMjBlc3RhdGV8ZW58MHx8fHwxNzkyNjY5ODc2fDA&ixlib=rb-4.1.0&q=85"
                  alt="Dubai skyline at dusk"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b1426]/70 via-transparent to-transparent" />
                <button data-testid="hero-play-btn" aria-label="Watch showreel" className="absolute inset-0 m-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#fba31b] flex items-center justify-center group">
                  <span className="absolute inset-0 rounded-full bg-[#fba31b]/50 animate-ping" />
                  <Play className="relative w-7 h-7 text-[#12203c] fill-[#12203c] ml-1" />
                </button>
                <div className="absolute bottom-4 left-4 font-code text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/80">
                  Showreel '26 — 60 sec
                </div>
              </div>

              <div className="absolute right-0 top-0 w-[32%] space-y-4 sm:space-y-5">
                <div className="animate-float rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                  <div className="relative">
                    <img src="https://images.unsplash.com/photo-1611048267451-e6ed903d4a38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NzA4Mzl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb20lMjByZWFsJTIwZXN0YXRlJTIwbHV4dXJ5JTIwdmlkZW8lMjB0aHVtYm5haWx8ZW58MHx8fHwxNzkyNjY5ODgwfDA&ixlib=rb-4.1.0&q=85" alt="Luxury living room" className="w-full h-24 sm:h-32 object-cover" />
                    <span className="absolute bottom-2 left-2 text-[10px] sm:text-xs font-bold bg-[#0b1426]/80 backdrop-blur px-2 py-1 rounded-md">Luxury Living</span>
                  </div>
                </div>
                <div className="animate-float-delay rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                  <img src="https://images.unsplash.com/photo-1580674684081-7617fbf3d745?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxkdWJhaSUyMGFyY2hpdGVjdHVyZSUyMHNreWxpbmUlMjBsdXh1cnklMjBwcm9wZXJ0eSUyMHJlYWwlMjBlc3RhdGV8ZW58MHx8fHwxNzkyNjY5ODc2fDA&ixlib=rb-4.1.0&q=85" alt="Penthouse balcony view" className="w-full h-24 sm:h-32 object-cover" />
                </div>
                <div className="animate-float rounded-2xl overflow-hidden border border-[#fba31b]/30 shadow-2xl" style={{ animationDelay: "0.6s" }}>
                  <div className="relative">
                    <img src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMGFyY2hpdGVjdHVyZSUyMHNreWxpbmUlMjBsdXh1cnklMjBwcm9wZXJ0eSUyMHJlYWwlMjBlc3RhdGV8ZW58MHx8fHwxNzkyNjY5ODc2fDA&ixlib=rb-4.1.0&q=85" alt="Dubai marina towers" className="w-full h-24 sm:h-32 object-cover" />
                    <span className="absolute bottom-2 left-2 text-[10px] sm:text-xs font-bold bg-[#fba31b] text-[#12203c] px-2 py-1 rounded-md">Your Next Investment</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          data-testid="hero-stats"
          className="mt-16 sm:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/5 pt-8"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-display text-3xl sm:text-4xl font-extrabold text-[#fba31b]">{s.value}</div>
              <div className="mt-1 text-xs sm:text-sm text-slate-400">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
